import React, { Component } from "react";
import { useLocation, useRouteError } from "react-router-dom";
import styles from "./ErrorBoundary.module.scss";
import Button from "components/Button/Button";

const CHUNK_RELOAD_KEY = "chunk_load_reload_attempted";

export function isChunkLoadError(error) {
  if (!error) return false;

  const message = error.message || String(error);

  return (
    error.name === "ChunkLoadError" ||
    message.includes("Loading chunk") ||
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Importing a module script failed")
  );
}

export function ErrorFallback({ error, errorMessage, onRetry, variant = "layout" }) {
  const isChunkError = isChunkLoadError(error);

  const title = isChunkError ? "Update available" : "Something went wrong";
  const message = isChunkError
    ? "A new version of the application is available. Please refresh the page to continue."
    : errorMessage || "Something went wrong. Please try again later.";

  const handleAction = () => {
    if (isChunkError) {
      window.location.reload();
      return;
    }

    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const wrapperClass =
    variant === "full" ? styles.fullScreenWrapper : styles.layoutWrapper;

  return (
    <div className={wrapperClass}>
      <h1 className={styles.errorHeader}>{title}</h1>
      <p className={styles.errorMessage}>{message}</p>
      {isChunkError && (
        <p className={styles.contactMessage}>
          This usually happens after a new deployment while an older version of
          the app is still open in your browser.
        </p>
      )}
      <p className={styles.button}>
        <Button onClick={handleAction}>
          {isChunkError ? "Refresh page" : "Try again"}
        </Button>
      </p>
    </div>
  );
}

export function RouteErrorFallback({ errorMessage, variant = "layout" }) {
  const error = useRouteError();
  return (
    <ErrorFallback
      error={error}
      errorMessage={errorMessage}
      variant={variant}
    />
  );
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error(error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    const { locationPathname } = this.props;

    if (
      locationPathname &&
      locationPathname !== prevProps.locationPathname &&
      this.state.hasError
    ) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          errorMessage={this.props.errorMessage}
          onRetry={this.handleRetry}
          variant={this.props.variant || "layout"}
        />
      );
    }

    return this.props.children;
  }
}

export function LayoutErrorBoundary({ children, ...props }) {
  const { pathname } = useLocation();

  return (
    <ErrorBoundary locationPathname={pathname} {...props}>
      {children}
    </ErrorBoundary>
  );
}

if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (event) => {
    if (!isChunkLoadError(event.reason)) return;

    const hasRetried = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (!hasRetried) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
      window.location.reload();
      return;
    }

    event.preventDefault();
  });
}

export default ErrorBoundary;
